import { getData, deleteData, patchData } from "../apiClient";

const API_URL = `assigned-employee`;

/**
 * ============================================================================
 * ASSIGNED EMPLOYEE MANAGEMENT API (menu_id: 98)
 * ============================================================================
 * APIs for managing employee assignments to rosters
 * ============================================================================
 */

/**
 * Get all active rosters for dropdown placement
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const GetAllRosterList = async (signal = null) => {
  try {
    const result = await getData(`${API_URL}/roster-list`, {}, {}, signal);
    return {
      data: {
        success: true,
        data: result?.data || result,
        status: 200,
      },
    };
  } catch (error) {
    console.error("Get Roster List API error:", error);
    return {
      data: {
        success: false,
        error: error.status === 400 ? error.data?.error : error.message,
        status: error.status,
      },
    };
  }
};

/**
 * Get paginated list of assigned employees
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search term
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const getAssignedEmployeesList = async (
  { page = 1, limit = 10, search = "" },
  signal = null,
) => {
  try {
    const queryParams = new URLSearchParams({ page, limit, search }).toString();
    const response = await getData(
      `${API_URL}/list?${queryParams}`,
      {},
      {},
      signal,
    );
    return {
      success: true,
      data: response?.data || [],
      ...response,
    };
  } catch (error) {
    console.error("Error fetching assigned employees list", error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Get assigned employees for a specific roster
 * @param {number} rosterId - Roster ID
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const getAssignedEmployees = async (rosterId, signal = null) => {
  try {
    const response = await getData(
      `${API_URL}/assigned/${rosterId}`,
      {},
      {},
      signal,
    );
    return {
      success: true,
      data: response?.data || [],
    };
  } catch (error) {
    console.error("Error fetching assigned employees for roster", error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Toggle assign/unassign employees for a roster
 * @param {Object} data - Assignment data
 * @param {number} data.roster_id - Roster ID
 * @param {Array<number>} data.employee_ids - Array of employee IDs
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const assignEmployees = async (data, signal = null) => {
  try {
    return {
      success: true,
      data: await patchData(`${API_URL}/employees`, data, {}, signal),
    };
  } catch (error) {
    return {
      data: {
        success: false,
        error: error.status === 400 ? error.data.error : error.message,
        status: error.status,
      },
    };
  }
};

/**
 * Get all employees for dropdown
 * @param {AbortSignal} signal - Abort signal for cancellation
 */
export const getEmployeesList = async (signal = null) => {
  try {
    const response = await getData(`${API_URL}/employees-list`, {}, {}, signal);
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error("Error fetching employees list", error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Delete an assigned employee
 * @param {Object} params - Delete parameters
 * @param {number} params.id - Employee ID to delete
 */
export const DeleteAssignedEmployee = async ({ id }) => {
  try {
    const result = await deleteData(`${API_URL}/employees/${id}`);
    return {
      data: {
        success: true,
        data: result,
      },
    };
  } catch (error) {
    console.error("Delete Assigned Employee API error:", error);
    return {
      data: {
        success: false,
        error: error.message,
      },
    };
  }
};
